import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { DEFAULT_TIMEZONE, ERROR_MESSAGES, STANDARD_DATE_FORMATS } from '../date-time-helpers.constants';
import type { DateInput, TimezoneOptions } from '../date-time-helpers.type';
import { formatDate } from '../formatting/formatting';
import { safeParseDateInput } from '../parsing/parsing';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Converts a date from one timezone to another
 *
 * @param {DateInput} date - Date to convert
 * @param {TimezoneOptions} options - Conversion options
 * @returns {Dayjs | null} Converted date in target timezone
 */
const convertTimezone = (date: DateInput, options: TimezoneOptions): Dayjs | null => {
  const parsedDate = safeParseDateInput(date);

  if (!parsedDate) {
    return null;
  }

  const { from, to = DEFAULT_TIMEZONE } = options;

  let convertedDate = parsedDate;

  // If source timezone is specified, set it
  if (from) {
    convertedDate = convertedDate.tz(from);
  }

  // Convert to target timezone
  return convertedDate.tz(to);
};

/**
 * Formats a date with timezone information
 *
 * @param {DateInput} date - Date to format
 * @param {string} [timezone=DEFAULT_TIMEZONE] - Target timezone
 * @param {string} [format=STANDARD_DATE_FORMATS.FULL_DATE] - Format string
 * @returns {string} Formatted date string with timezone
 */
const formatDateWithTimezone = (
  date: DateInput,
  timezone = DEFAULT_TIMEZONE,
  format = STANDARD_DATE_FORMATS.FULL_DATE,
): string => {
  return formatDate(date, { timezone, format });
};

/**
 * Gets the current timezone
 *
 * @returns {string} Current timezone name
 */
const getCurrentTimezone = (): string => {
  return dayjs.tz.guess();
};

/**
 * Checks if a timezone is valid
 *
 * @param {string} timezone - Timezone to validate
 * @returns {boolean} True if timezone is valid
 */
const isValidTimezone = (timezone: string): boolean => {
  try {
    // Use current date to test timezone validity
    dayjs().tz(timezone);
    return true;
  } catch {
    return false;
  }
};

/**
 * Gets the timezone offset in minutes
 *
 * @param {string} timezone - Timezone to get offset for
 * @param {DateInput} [date] - Date to calculate offset (defaults to now)
 * @returns {number} Offset in minutes
 */
const getTimezoneOffset = (timezone: string, date?: DateInput): number => {
  const targetDate = safeParseDateInput(date) || dayjs();

  return targetDate.tz(timezone).utcOffset();
};

/**
 * Gets the timezone abbreviation
 *
 * @param {string} timezone - Timezone to get abbreviation for
 * @param {DateInput} [date] - Date to calculate abbreviation (defaults to now)
 * @returns {string} Timezone abbreviation
 */
const getTimezoneAbbreviation = (timezone: string, date?: DateInput): string => {
  const targetDate = safeParseDateInput(date) || dayjs();

  return formatDate(targetDate, { timezone, format: 'z' });
};

/**
 * Converts a 24-hour time string to 12-hour format
 *
 * @param {string} timeString - 24-hour time string (e.g., "14:30")
 * @returns {string} 12-hour formatted time string (e.g., "02:30 PM")
 */
const convertTo12HourFormat = (timeString: string): string => {
  if (!timeString || typeof timeString !== 'string') {
    return ERROR_MESSAGES.INVALID_DATE;
  }

  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Validate hours and minutes
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 24 || minutes < 0 || minutes > 59) {
    return ERROR_MESSAGES.INVALID_DATE;
  }

  // Handle 24:00 as midnight
  const adjustedHours = hours === 24 ? 0 : hours;

  // Create a Date object with the current date and the given time
  const dateObj = new Date();
  dateObj.setHours(adjustedHours);
  dateObj.setMinutes(minutes);

  // Use the date.toLocaleTimeString method with the desired options
  const formattedTimeString = dateObj.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return formattedTimeString;
};

/**
 * Converts a time string to UTC time
 *
 * @param {string} timeString - Time string in HH:mm format
 * @returns {string} UTC equivalent time string
 */
const getUTCTime = (timeString: string): string => {
  if (!timeString || typeof timeString !== 'string') {
    return ERROR_MESSAGES.INVALID_DATE;
  }

  const [hours, minutes] = timeString.split(':').map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    return ERROR_MESSAGES.INVALID_DATE;
  }

  const currentDate = dayjs();
  const newDate = currentDate.set('hour', hours).set('minute', minutes);
  return newDate.utc().format('HH:mm');
};

/**
 * Converts a date to local timezone with enhanced fallback handling
 * Returns current local time if input is invalid
 *
 * @param {DateInput} dateInput - Date to convert
 * @returns {Dayjs} Local timezone dayjs object
 */
const toLocalDate = (dateInput: DateInput): Dayjs => {
  const parsedDate = safeParseDateInput(dateInput);

  if (!parsedDate) {
    return dayjs();
  }

  return parsedDate.utc(true).local();
};

/**
 * Converts a date to UTC timezone with enhanced fallback handling
 * Returns current UTC time if input is invalid
 *
 * @param {DateInput} dateInput - Date to convert
 * @returns {Dayjs} UTC timezone dayjs object
 */
const toUTCDate = (dateInput: DateInput): Dayjs => {
  const parsedDate = safeParseDateInput(dateInput);

  if (!parsedDate) {
    return dayjs().utc(true);
  }

  return parsedDate.utc(true);
};

export {
  convertTimezone,
  convertTo12HourFormat,
  formatDateWithTimezone,
  getCurrentTimezone,
  getTimezoneAbbreviation,
  getTimezoneOffset,
  getUTCTime,
  isValidTimezone,
  toLocalDate,
  toUTCDate,
};
